import { type FC, Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Header } from '@/components/Header/Header';
import { MobileTabBar } from '@/components/AppNav';
import { SkipLink } from '@/components/SkipLink';
import { AnnouncerProvider } from '@/components/LiveRegion';
import { cn } from '@/utils/cn';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Footer } from '@/components/Footer/Footer';
import { AppFooter } from '@/components/AppFooter';
import { SectionLayout, BareLayout } from '@/components/SectionLayout';
import { PlayRedirect, ProfileRedirect, SettingsRedirect } from '@/navigation/redirects';
import { TermsUpdateModal } from '@/components/TermsUpdateModal';
import { WhatsNewModal } from '@/components/WhatsNew';
import { hasUnseenRelease } from '@/content/whatsNew';
import { AdminRoute } from '@/components/AdminRoute/AdminRoute';
import { getAcceptanceStatus } from '@/services/terms';
import { rememberReturnPath } from '@/utils/returnPath';

// Pages
import { Home } from '@/pages/Home';
// Premium pages
// Admin pages

// Pages load on demand so the first paint ships only the shell
const Login = lazy(() => import('@/pages/Login').then((m) => ({ default: m.Login })));
const Register = lazy(() => import('@/pages/Register').then((m) => ({ default: m.Register })));
const Waitlist = lazy(() => import('@/pages/Waitlist').then((m) => ({ default: m.Waitlist })));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail').then((m) => ({ default: m.VerifyEmail })));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword').then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('@/pages/ResetPassword').then((m) => ({ default: m.ResetPassword })));
const AuthError = lazy(() => import('@/pages/AuthError').then((m) => ({ default: m.AuthError })));
const AuthCallback = lazy(() => import('@/pages/AuthCallback').then((m) => ({ default: m.AuthCallback })));
const Battles = lazy(() => import('@/pages/Battles').then((m) => ({ default: m.Battles })));
const BattleNew = lazy(() => import('@/pages/BattleNew').then((m) => ({ default: m.BattleNew })));
const Battle = lazy(() => import('@/pages/Battle').then((m) => ({ default: m.Battle })));
const BattleReport = lazy(() => import('@/pages/BattleReport').then((m) => ({ default: m.BattleReport })));
const Tools = lazy(() => import('@/pages/Tools').then((m) => ({ default: m.Tools })));
const ToolPage = lazy(() => import('@/pages/ToolPage').then((m) => ({ default: m.ToolPage })));
const Rosters = lazy(() => import('@/pages/Rosters').then((m) => ({ default: m.Rosters })));
const RosterNew = lazy(() => import('@/pages/RosterNew').then((m) => ({ default: m.RosterNew })));
const RosterDetail = lazy(() => import('@/pages/RosterDetail').then((m) => ({ default: m.RosterDetail })));
const RosterPrint = lazy(() => import('@/pages/RosterPrint').then((m) => ({ default: m.RosterPrint })));
const Collection = lazy(() => import('@/pages/Collection').then((m) => ({ default: m.Collection })));
const CollectionDetail = lazy(() => import('@/pages/CollectionDetail').then((m) => ({ default: m.CollectionDetail })));
const YouLayout = lazy(() => import('@/pages/You/YouLayout').then((m) => ({ default: m.YouLayout })));
const PlanUsage = lazy(() => import('@/pages/You/PlanUsage').then((m) => ({ default: m.PlanUsage })));
const Friends = lazy(() => import('@/pages/Friends').then((m) => ({ default: m.Friends })));
const Achievements = lazy(() => import('@/pages/Achievements').then((m) => ({ default: m.Achievements })));
const Analytics = lazy(() => import('@/pages/Analytics').then((m) => ({ default: m.Analytics })));
const Welcome = lazy(() => import('@/pages/Welcome').then((m) => ({ default: m.Welcome })));
const HomeDashboard = lazy(() => import('@/pages/HomeDashboard').then((m) => ({ default: m.HomeDashboard })));
const BattleLog = lazy(() => import('@/pages/BattleLog').then((m) => ({ default: m.BattleLog })));
const Journal = lazy(() => import('@/pages/Journal').then((m) => ({ default: m.Journal })));
const Community = lazy(() => import('@/pages/Community').then((m) => ({ default: m.Community })));
const Projects = lazy(() => import('@/pages/Projects').then((m) => ({ default: m.Projects })));
const ProjectNew = lazy(() => import('@/pages/ProjectNew').then((m) => ({ default: m.ProjectNew })));
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail').then((m) => ({ default: m.ProjectDetail })));
const Help = lazy(() => import('@/pages/Help').then((m) => ({ default: m.Help })));
const Settings = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.Settings })));
const Profile = lazy(() => import('@/pages/Profile').then((m) => ({ default: m.Profile })));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy })));
const TermsAndConditions = lazy(() => import('@/pages/TermsAndConditions').then((m) => ({ default: m.TermsAndConditions })));
const About = lazy(() => import('@/pages/About').then((m) => ({ default: m.About })));
const Donate = lazy(() => import('@/pages/Donate').then((m) => ({ default: m.Donate })));
const PaintRecipes = lazy(() => import('@/pages/PaintRecipes').then((m) => ({ default: m.PaintRecipes })));
const HobbyDashboard = lazy(() => import('@/pages/HobbyDashboard').then((m) => ({ default: m.HobbyDashboard })));
const Grimm = lazy(() => import('@/pages/Grimm').then((m) => ({ default: m.Grimm })));
const UnitDetail = lazy(() => import('@/pages/UnitDetail').then((m) => ({ default: m.UnitDetail })));
const NearMe = lazy(() => import('@/pages/NearMe').then((m) => ({ default: m.NearMe })));
const VenueDetail = lazy(() => import('@/pages/VenueDetail').then((m) => ({ default: m.VenueDetail })));
const EventDetail = lazy(() => import('@/pages/EventDetail').then((m) => ({ default: m.EventDetail })));
const PublicRegions = lazy(() => import('@/pages/PublicRegions').then((m) => ({ default: m.PublicRegions })));
const PublicArea = lazy(() => import('@/pages/PublicArea').then((m) => ({ default: m.PublicArea })));
const PublicVenue = lazy(() => import('@/pages/PublicVenue').then((m) => ({ default: m.PublicVenue })));
const VenueManage = lazy(() => import('@/pages/VenueManage').then((m) => ({ default: m.VenueManage })));
const NearMeSubmit = lazy(() => import('@/pages/NearMeSubmit').then((m) => ({ default: m.NearMeSubmit })));
const Premium = lazy(() => import('@/pages/Premium').then((m) => ({ default: m.Premium })));
const PremiumSuccess = lazy(() => import('@/pages/Premium').then((m) => ({ default: m.Success })));
const PremiumCancel = lazy(() => import('@/pages/Premium').then((m) => ({ default: m.Cancel })));
const AdminDashboard = lazy(() => import('@/pages/Admin').then((m) => ({ default: m.AdminDashboard })));
const AdminCommunity = lazy(() => import('@/pages/Admin').then((m) => ({ default: m.AdminCommunity })));
const AdminVenueManagers = lazy(() => import('@/pages/Admin').then((m) => ({ default: m.AdminVenueManagers })));
const AdminSeedImport = lazy(() => import('@/pages/Admin').then((m) => ({ default: m.AdminSeedImport })));
const AdminNearMe = lazy(() => import('@/pages/Admin').then((m) => ({ default: m.AdminNearMe })));
const AdminSettings = lazy(() => import('@/pages/Admin').then((m) => ({ default: m.AdminSettings })));
const AdminUsers = lazy(() => import('@/pages/Admin').then((m) => ({ default: m.AdminUsers })));

/**
 * RequireAuth - layout route for pages that need a signed-in user
 */
const RequireAuth: FC = () => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Send them back here after signing in (an email link or a bookmark, for example)
    rememberReturnPath(location.pathname + location.search + location.hash);
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

/**
 * Public pages keep the full marketing footer, even for signed-in visitors.
 */
const isLegalPath = (pathname: string): boolean => ['/privacy-policy', '/terms-and-conditions', '/about'].includes(pathname);

const isPublicPath = (pathname: string): boolean =>
  ['/', '/login', '/register', '/waitlist', '/forgot-password', '/about', '/donate', '/privacy-policy', '/terms-and-conditions'].includes(pathname) ||
  pathname.startsWith('/premium') ||
  pathname === '/events' ||
  pathname.startsWith('/events/') ||
  pathname.startsWith('/venues/') ||
  pathname.startsWith('/auth/');

/**
 * HomeRoute - signed-in users land on Home; visitors see the public home page
 */
const HomeRoute: FC = () => {
  const { isAuthenticated, isLoading } = useAuthStore();
  if (!isLoading && isAuthenticated) return <Navigate to="/home" replace />;
  return <Home />;
};

/**
 * App - Main application component
 *
 * Sets up routing, authentication check, layout, and navigation.
 */
export const App: FC = () => {
  usePageTitle();
  const { loadUser, isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  const [showTermsUpdate, setShowTermsUpdate] = useState(false);
  // The wizard waits for the terms check: terms first, then onboarding
  const [termsResolved, setTermsResolved] = useState(false);

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Check if terms acceptance is needed
  useEffect(() => {
    const checkTermsStatus = async () => {
      if (!isAuthenticated || !user) {
        setShowTermsUpdate(false);
        setTermsResolved(false);
        return;
      }

      try {
        const status = await getAcceptanceStatus();
        setShowTermsUpdate(status.needsAcceptance);
      } catch (error) {
        // Don't block the app if terms check fails - log and continue
        console.warn('Failed to check terms status:', error);
        setShowTermsUpdate(false);
      }
      setTermsResolved(true);
    };

    checkTermsStatus();
  }, [isAuthenticated, user]);

  // Handle terms acceptance
  const handleTermsAccepted = () => {
    setShowTermsUpdate(false);
  };

  // Routes where header should be hidden (auth callbacks and transient pages only)
  const hideHeaderRoutes = [
    '/auth/error',
    '/auth/callback',
  ];
  const onWelcome = location.pathname === '/welcome';
  const shouldShowHeader = !hideHeaderRoutes.includes(location.pathname) && !location.pathname.endsWith('/print') && !onWelcome;

  // While the wizard is required, everything except the wizard, the legal pages and auth screens goes to /welcome
  const wizardAllowed = onWelcome || isLegalPath(location.pathname) || location.pathname.startsWith('/auth/');
  const mustOnboard = isAuthenticated && user?.onboarding_required === true && termsResolved && !showTermsUpdate && !wizardAllowed;

  // The "What's new" pop-up comes last: after terms and the wizard, and never over a live battle or a print view
  const quietPath = /^\/battles\/[^/]+$/.test(location.pathname) && location.pathname !== '/battles/new' && location.pathname !== '/battles/log';
  const canShowWhatsNew =
    isAuthenticated && !!user && user.onboarding_required === false && termsResolved && !showTermsUpdate &&
    !wizardAllowed && !quietPath && !location.pathname.endsWith('/print');
  const whatsNewDue = hasUnseenRelease(user?.whats_new_seen_version);

  // Routes where terms modal should NOT be shown (so users can view the documents)
  const legalRoutes = [
    '/terms-and-conditions',
    '/privacy-policy',
    '/about',
  ];
  const isOnLegalPage = legalRoutes.includes(location.pathname);

  return (
    <AnnouncerProvider>
    <div className={cn(
      'min-h-screen bg-bg-primary flex flex-col',
      // Clear the fixed mobile tab bar so the footer's last line can scroll above it
      isAuthenticated && shouldShowHeader && 'pb-[calc(var(--tab-bar-height)+env(safe-area-inset-bottom))] sm:pb-0',
    )}>
      <SkipLink />
      {/* Navigation Header - shown on all pages except login/error */}
      {shouldShowHeader && <Header />}

      {/* Terms Update Modal - shown when user needs to accept updated terms (not on legal pages) */}
      <TermsUpdateModal
        isOpen={showTermsUpdate && !isOnLegalPage}
        onAccept={handleTermsAccepted}
      />

      {whatsNewDue && <WhatsNewModal canShow={canShowWhatsNew} />}

      {/* Main content: each layout route owns (or its pages render) the single main landmark */}
      {mustOnboard ? <Navigate to="/welcome" replace /> : (
      <Suspense fallback={<div className="container-app py-8 text-text-secondary" role="status">Loading…</div>}>
        <Routes>
          {/* Public routes and screens with their own shell */}
          <Route element={<BareLayout />}>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/auth/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/auth/error" element={<AuthError />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/about" element={<About />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/events" element={<PublicRegions />} />
            <Route path="/events/:country/:area" element={<PublicArea />} />
            <Route path="/events/:country/:area/:town" element={<PublicArea />} />
            <Route path="/venues/:id/:slug" element={<PublicVenue />} />
            <Route path="/premium" element={<Premium />} />
            <Route path="/premium/success" element={<PremiumSuccess />} />
            <Route path="/premium/cancelled" element={<PremiumCancel />} />

            {/* Signed-in screens that keep their own shell (live battle, print) */}
            <Route element={<RequireAuth />}>
              <Route path="/battles/:id" element={<Battle />} />
              <Route path="/rosters/:id/print" element={<RosterPrint />} />
            </Route>
          </Route>

          {/* Redirects from the pre-/you URLs */}
          <Route path="/profile" element={<ProfileRedirect />} />
          <Route path="/settings" element={<SettingsRedirect />} />
          <Route path="/friends" element={<Navigate to="/you/friends" replace />} />
          <Route path="/achievements" element={<Navigate to="/you/achievements" replace />} />
          <Route path="/analytics" element={<Navigate to="/you/stats" replace />} />
          <Route path="/play" element={<PlayRedirect />} />
          <Route path="/you" element={<Navigate to="/you/profile" replace />} />

          {/* Signed-in routes. Pages render <Page>, which is the main landmark. */}
          <Route element={<RequireAuth />}>
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/home" element={<HomeDashboard />} />
            <Route element={<SectionLayout section="hobby" />}>
              <Route path="/hobby" element={<HobbyDashboard />} />
              <Route path="/collection" element={<Collection />} />
              <Route path="/collection/:gameSystemId" element={<CollectionDetail />} />
              <Route path="/collection/:gameSystemId/items/:itemId" element={<UnitDetail />} />
              <Route path="/paint-recipes" element={<PaintRecipes />} />
              <Route path="/journal" element={<Journal />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/community" element={<Community />} />
              <Route path="/projects/new" element={<ProjectNew />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
            </Route>

            <Route element={<SectionLayout section="play" />}>
              <Route path="/battles" element={<Battles />} />
              <Route path="/battles/new" element={<BattleNew />} />
              <Route path="/battles/log" element={<BattleLog />} />
              <Route path="/battles/log/:id" element={<BattleLog />} />
              <Route path="/battles/:id/report" element={<BattleReport />} />
              <Route path="/rosters" element={<Rosters />} />
              <Route path="/rosters/new" element={<RosterNew />} />
              <Route path="/rosters/:id" element={<RosterDetail />} />
              <Route path="/near-me" element={<NearMe />} />
              <Route path="/near-me/venues/:id" element={<VenueDetail />} />
              <Route path="/near-me/venues/:id/manage" element={<VenueManage />} />
              <Route path="/near-me/events/:id" element={<EventDetail />} />
              <Route path="/near-me/submit" element={<NearMeSubmit />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools/:toolId" element={<ToolPage />} />
            </Route>

            <Route path="/grimm" element={<Grimm />} />

            <Route element={<YouLayout />}>
              <Route path="/you/profile" element={<Profile />} />
              <Route path="/you/friends" element={<Friends />} />
              <Route path="/you/achievements" element={<Achievements />} />
              <Route path="/you/stats" element={<Analytics />} />
              <Route path="/you/settings" element={<Settings />} />
              <Route path="/you/plan" element={<PlanUsage />} />
              <Route path="/help" element={<Help />} />
            </Route>
          </Route>

          {/* Admin routes - require platform_admin role */}
          <Route element={<BareLayout />}>
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
            <Route path="/admin/community" element={<AdminRoute><AdminCommunity /></AdminRoute>} />
            <Route path="/admin/near-me/venues/:id" element={<AdminRoute><AdminVenueManagers /></AdminRoute>} />
            <Route path="/admin/near-me/seed" element={<AdminRoute><AdminSeedImport /></AdminRoute>} />
            <Route path="/admin/near-me" element={<AdminRoute><AdminNearMe /></AdminRoute>} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      )}

      {isAuthenticated && shouldShowHeader && <MobileTabBar />}

      {/* Footer - shown on all pages except print */}
      {!location.pathname.endsWith('/print') && !onWelcome && (isAuthenticated && !isPublicPath(location.pathname) ? <AppFooter /> : <Footer />)}
    </div>
    </AnnouncerProvider>
  );
};
